import { Icon } from '@dynamic/ui';
import styled from 'styled-components';

import { MEDIA_QUERIES } from '#/hooks';
import { remify } from '#/service/utils';

const DOT_SIZE = remify(40);
const LINE_THICKNESS = remify(3);
const STEP_MAX_WIDTH = remify(198);

// main container of the whole step indicator
export const StepIndicatorContainer = styled.div.attrs({
  'data-testid': 'step-indicator',
})`
  display: flex;
  width: 100%;
  margin-inline: auto;
`;

StepIndicatorContainer.displayName = 'StepIndicatorContainer';

// container for one step, including the number, the lines, and the title
export const StepContainer = styled.div.attrs({
  'data-testid': 'step-container',
})`
  width: 100%;
  max-width: ${STEP_MAX_WIDTH};
  min-width: calc(${DOT_SIZE} * 1.5);
  text-align: center;
  font: var(--fontStyleCallout);
`;

StepContainer.displayName = 'StepContainer';

// container for the top row of the step, including the number and the lines
export const StepTop = styled.div`
  display: flex;
  justify-content: center;
`;

StepTop.displayName = 'StepTop';

interface StepNumberProps {
  isCurrent?: boolean;
  isDone?: boolean;
}

// step number in a circle
export const StepNumber = styled.div.attrs({
  'data-testid': 'step-number',
})<StepNumberProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${DOT_SIZE};
  height: ${DOT_SIZE};
  z-index: 1;
  border-radius: 100rem;
  background: ${({ isCurrent, isDone }) =>
    isCurrent || isDone
      ? 'var(--buttonPrimaryBackground)'
      : 'var(--buttonSecondaryBackground)'};
  color: ${({ isCurrent, isDone }) =>
    isCurrent || isDone
      ? 'var(--buttonPrimaryForeground)'
      : 'var(--buttonSecondaryForeground)'};
`;

StepNumber.displayName = 'StepNumber';

interface StepLineProps {
  isVisible?: boolean;
  isDone?: boolean;
}

// lines between the step numbers
export const StepLine = styled.div.attrs({
  'data-testid': 'step-line',
})<StepLineProps>`
  display: inline-flex;
  flex: 0 20 100rem;
  height: ${LINE_THICKNESS};
  margin-block: calc((${DOT_SIZE} - ${LINE_THICKNESS}) / 2);
  opacity: ${({ isVisible = true }) => (isVisible ? 1 : 0)};
  background: ${({ isDone }) =>
    isDone
      ? 'var(--buttonPrimaryBackground)'
      : 'var(--buttonSecondaryBackground)'};
`;

StepLine.displayName = 'StepLine';

// title of the step
export const StepTitle = styled.div.attrs({
  'data-testid': 'step-title',
})`
  grid-area: title;
  margin-top: ${remify(8)};
  padding-inline: 0.1rem;

  @media ${MEDIA_QUERIES.xs} {
    font: var(--fontStyleCaption2);
  }
`;

StepTitle.displayName = 'StepTitle';

// checkmark icon for steps that are done
export const StepDoneIcon = styled(Icon).attrs({
  name: 'checkmark',
  'data-testid': 'step-done-icon',
})`
  width: 1rem;
  height: 1rem;
`;
